import { useEffect, useState } from 'react';
import {
  Divider,
  Stack,
  DialogContent,
  DialogActions,
  Dialog,
  Button,
  Checkbox,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingButton } from '@mui/lab';

import { Strings } from '@app/constants';
import { Header } from '@app/sections/header';
import { useTable } from '@app/components/table';
import { ISku } from '../types';
import { addNewSkuProduct } from '../slice';
import { selectSkuState } from '@app/modules/sku/selectors';
import { getSku } from '@app/modules/sku';
import { selectAddNewSkuProductState } from '../selectors';
import { SearchBar } from '@app/components';
import { TruncatedTypography } from '@app/components/typography';
import { useRequestState } from '@app/hooks';

interface Props {
  open: boolean;
  onClose: VoidFunction;
  row?: ISku;
  bannerId: number;
}

const AddProductInBanner = ({ open, onClose, row, bannerId }: Props) => {
  const dispatch = useDispatch();
  const tableProps = useTable();
  const page = tableProps.page;

  const handleSearchChange = (event: any) => {
    setQuery(event.target.value);
  };

  const [query, setQuery] = useState('');

  useEffect(() => {
    if (query.length !== 0) {
      dispatch(getSku({ page, q: query }));
    } else {
      dispatch(getSku({ page }));
    }
  }, [dispatch, page, query]);

  const newSkuState = useSelector(selectSkuState);
  const [selectedItemIds, setSelectedItemIds] = useState<number[]>([]);

  const handleCheckboxChange = (itemId: number) => {
    setSelectedItemIds((prevSelectedIds) => {
      if (prevSelectedIds.includes(itemId)) {
        return prevSelectedIds.filter((id) => id !== itemId);
      } else {
        return [...prevSelectedIds, itemId];
      }
    });
  };

  const handleClick = () => {
    dispatch(addNewSkuProduct({ skuId: selectedItemIds, id: bannerId }));
  };

  const { loading } = useRequestState({
    stateSelector: selectAddNewSkuProductState,
    onSuccess: onClose,
    successMessageShown: true,
    errorShown: true,
  });

  return (
    <>
      <Dialog open={open} fullWidth>
        <Header variant="dialog" title={Strings.button.addProductInBanner} onClose={onClose} />

        <Divider />

        <SearchBar
          sx={{ padding: '2% 7% 0% 2%' }}
          onChange={handleSearchChange}
          placeholder={Strings.common.search}
        />

        <DialogContent>
          <Stack spacing={2} my={2}>
            {newSkuState?.data?.map((item: any, index: number) => {
              return (
                <>
                  <Stack display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
                    <Stack
                      sx={{ width: '170px' }}
                      key={index}
                      onClick={() => {
                        handleCheckboxChange(item.id);
                      }}
                    >
                      <TruncatedTypography text={item.skuId} lines={1} />
                    </Stack>
                    <Stack
                      sx={{
                        width: '300px',
                        display: 'flex',
                        flexWrap: 'wrap',
                      }}
                      key={index}
                      onClick={() => {
                        handleCheckboxChange(item.id);
                      }}
                    >
                      <TruncatedTypography text={item?.name ? item.name : 'NA'} lines={1} />
                    </Stack>
                    <Checkbox
                      checked={selectedItemIds.includes(item.id)}
                      onChange={() => {
                        handleCheckboxChange(item.id);
                      }}
                    />
                  </Stack>
                </>
              );
            })}
          </Stack>
        </DialogContent>

        <Divider />

        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={onClose}>
            {Strings.button.cancel}
          </Button>

          <LoadingButton loading={loading} type="submit" variant="contained" onClick={handleClick}>
            {Strings.button.save}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddProductInBanner;
